import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import { isDefined } from 'remeda';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';
import {
  buildPreviewReminderParams,
  type PreviewReminderVariables,
} from './preview-reminder-request';

export type PreviewReminderPdfVariables = PreviewReminderVariables;

// #region Response
const ReminderPdfPreviewResponseSchema = z.object({
  data: z.object({
    pdf: z
      .string()
      .nullish()
      .transform(value => value ?? undefined),
  }),
});

export type ReminderPdfPreview = z.infer<typeof ReminderPdfPreviewResponseSchema>['data'];
// #endregion Response

const PLACEHOLDER: ReminderPdfPreview = { pdf: undefined };

// MARK: Load function
async function previewReminderPdfQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: PreviewReminderPdfVariables,
): Promise<ReminderPdfPreview> {
  const instance = await axiosInstance;
  const { data } = await instance.get(`invoices/${variables.invoiceId}/reminder_pdf_preview`, {
    params: buildPreviewReminderParams(variables),
  });

  const parseResult = ReminderPdfPreviewResponseSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'reminderPdfPreview');
    throw new SilentError();
  }

  return parseResult.data.data;
}

// MARK: Hook
export function usePreviewReminderPdf(
  variables: PreviewReminderPdfVariables,
  options: QueryOptions<ReminderPdfPreview> = {},
) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<ReminderPdfPreview, ApiError>({
    queryKey: ['reminderPdfPreview', variables],
    queryFn: () => previewReminderPdfQueryFn(axiosInstance, variables),
    placeholderData: previousData => (isDefined(previousData) ? previousData : PLACEHOLDER),
    ...options,
  });

  return addResourceNameToQueryResult<
    ReminderPdfPreview,
    ApiError,
    'reminderPdfPreview',
    'PreventUndefined'
  >('reminderPdfPreview', queryResult, PLACEHOLDER);
}
